










































































































































































import Vue, { PropType } from 'vue';
import Datepicker from 'vuejs-datepicker';
import { ja } from 'vuejs-datepicker/dist/locale';
import { LaborCalendarEventKind } from '@/class/models/typed/labor';
import { ICustomHolidayView } from '@/class/models/typed/custom-holiday';

// ===== 型定義 =====
// tslint:disable interface-over-type-literal
type EventBase = {
  id: string | number;
  start: string; // "HH:mm"
  end: string; // "HH:mm"
  isAllDay?: boolean; // 終日フラグ
};

export type WorkEvent = EventBase & {
  kind: typeof LaborCalendarEventKind.WORK;
  title: string;
  description?: string;
};

export type AbsenceEvent = EventBase & {
  kind: typeof LaborCalendarEventKind.ABSENCE;
  absenceType: string;
  note?: string;
};

export type DayEvent = WorkEvent | AbsenceEvent;

function normalizeDate(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}

function getToday(): Date {
  return normalizeDate(new Date());
}

const MIN_EVENT_MINUTES = 30;
const ALL_DAY_START_MIN = 9 * 60;
const ALL_DAY_END_MIN = 18 * 60;

export default Vue.extend({
  name: 'DayTimeLine',
  components: { Datepicker },
  props: {
    placement: {
      type: String as PropType<'left' | 'right'>,
      default: 'right',
    },
    customHolidays: {
      type: Array as PropType<ICustomHolidayView[]>,
      default: () => [],
    },
    date: { type: Date, required: true },
    userInfo: {
      type: Object,
      default: () => ({}),
    },
    events: {
      type: Array as PropType<DayEvent[]>,
      default: () => [],
    },
    startHour: { type: Number, default: 0 },
    endHour: { type: Number, default: 24 },
    rowHeight: { type: Number, default: 90 }, // 60分あたりの基準高さ(px)
    showActions: {
      type: Boolean,
      default: true,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      LaborCalendarEventKind,
      ja,
      localDate: this.date as any,
      tooltipEvent: null as DayEvent | null,
      tooltipStyle: { top: '0px', left: '0px' } as {
        top: string;
        left: string;
      },
      selectData: null as DayEvent,
    };
  },
  watch: {
    date(d: Date) {
      this.localDate = d;
    },
    events(newVal) {
      this.$data.selectData = newVal[0];
    },
  },
  computed: {
    disabledDates() {
      return {
        // 未来の日付は選択できないようにする
        from: getToday(),
      };
    },
    canGoNext(): boolean {
      const nextDay = new Date(this.localDate);
      // 一日加算
      nextDay.setDate(nextDay.getDate() + 1);
      const normalizedNextDay = normalizeDate(nextDay);
      // 未来の日付には遷移させないようにする
      if (normalizedNextDay > getToday()) {
        return false;
      }
      return true;
    },
    hours(): number[] {
      const res: number[] = [];
      for (let h = this.startHour; h < this.endHour; h++) {
        res.push(h);
      }
      return res;
    },
    rowPx(): number {
      return this.rowHeight;
    },
    sortedEvents(): DayEvent[] {
      return [...this.events].sort((a, b) => this.toMinutes(a.start) - this.toMinutes(b.start));
    },

    /**
     * 実時間 -> 表示用時間 への変換テーブル
     *
     * 1分イベントでも最低30分の高さを確保するため、
     * イベントが存在する minute 区間だけ拡大率を上げる。
     *
     */
    visualOffsets(): number[] {
      const baseMin = this.startHour * 60;
      const limitMin = this.endHour * 60;
      const totalMin = Math.max(limitMin - baseMin, 0);

      // 「1分ごとの表示倍率」を管理する配列で、初期値は全部 1（等倍）
      //  例: 8時〜20時なら 720分 -> [1, 1, 1, ..., 1] (720個)
      const minuteScales = new Array<number>(totalMin).fill(1);

      this.events.forEach(ev => {
        const { startMin, endMin } = this.getEventRange(ev);

        const clampedStart = Math.max(startMin, baseMin);
        const clampedEnd = Math.min(endMin, limitMin);
        const duration = clampedEnd - clampedStart;
        if (duration <= 0) {
          return;
        }

        // 30分未満のイベントだけ、その区間を引き伸ばす
        // 拡大倍率 = (30分 / イベントの実時間)
        // 例: 1分イベントなら 30倍 → 30 / 1 = 30倍
        const scale = duration < MIN_EVENT_MINUTES ? MIN_EVENT_MINUTES / duration : 1;

        if (scale <= 1) {
          return;
        }

        // タイムライン上の各分数ごとの拡大倍率を計算
        for (let m = clampedStart; m < clampedEnd; m++) {
          const idx = m - baseMin;
          if (idx >= 0 && idx < minuteScales.length) {
            minuteScales[idx] = Math.max(minuteScales[idx], scale);
          }
        }
      });

      // 実時間 minute -> 表示用 minute
      const offsets: number[] = [0];
      for (let i = 0; i < minuteScales.length; i++) {
        // 例: offsets[542] = offsets[541] + minuteScales[541]
        // 30倍 → offsets[542] = offsets[541] + 30
        offsets.push(offsets[i] + minuteScales[i]);
      }
      return offsets;
    },
  },
  methods: {
    openTooltip(ev: DayEvent, event: MouseEvent) {
      const target = event.currentTarget as HTMLElement;
      if (!target) { return; }

      const isOverflowing = target.scrollHeight > target.clientHeight || target.scrollWidth > target.clientWidth;

      if (!isOverflowing) {
        this.tooltipEvent = null;
        return;
      }

      const rect = target.getBoundingClientRect();

      this.tooltipEvent = ev;
      this.$nextTick(() => {
        const tooltipEl = this.$el.querySelector('.ds-tooltip') as HTMLElement;
        if (!tooltipEl) { return; }

        const tooltipHeight = tooltipEl.offsetHeight;
        const tooltipWidth = tooltipEl.offsetWidth;

        // 中央基準で半分上にずらす
        let top = rect.top + rect.height / 2 - tooltipHeight / 2;

        // 上にはみだしそうな時、8pxの位置から表示する
        if (top < 8) {
          top = 8;
        }
        // 下にはみだしそうな時、ウィンドウの下から8px上の位置から表示する
        if (top + tooltipHeight > window.innerHeight - 8) {
          top = window.innerHeight - tooltipHeight - 8;
        }

        // 横の位置
        let left: number;

        if (this.placement === 'left') {
          // 左に出す
          left = rect.left - tooltipWidth - 8;

          // はみ出し防止
          if (left < 8) { left = 8; }
        } else {
          // 右に出す
          left = rect.right + 8;

          // はみ出し防止
          if (left + tooltipWidth > window.innerWidth - 8) {
            left = window.innerWidth - tooltipWidth - 8;
          }
        }

        this.tooltipStyle = {
          top: `${top}px`,
          left: `${left}px`,
        };
      });
    },
    closeTooltip() {
      this.tooltipEvent = null;
    },
    formatDate(d: Date | null): string {
      if (!d) {
        return '';
      }
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}/${m}/${day}`;
    },

    onPickDate(d: Date | null) {
      this.$emit('change-date', d);
    },

    onCalendarMove(d: Date | null) {
      this.$emit('calender-move', d);
    },

    onPrev() {
      const d = new Date(this.localDate);
      d.setDate(d.getDate() - 1);
      this.$emit('change-date', d);
    },

    onNext() {
      const d = new Date(this.localDate);
      d.setDate(d.getDate() + 1);
      const next = normalizeDate(d);
      this.$emit('change-date', next);
    },

    toMinutes(hhmm: string): number {
      const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
      return h * 60 + (m || 0);
    },

    getEventRange(ev: DayEvent): { startMin: number; endMin: number } {
      if (ev.isAllDay) {
        return {
          startMin: ALL_DAY_START_MIN,
          endMin: ALL_DAY_END_MIN,
        };
      }

      return {
        startMin: this.toMinutes(ev.start),
        endMin: this.toMinutes(ev.end),
      };
    },
    // 実時間の分から、表示上の経過分(visualOffsets)を求め、タイムライン上の位置(px)に変換する。
    minuteToPx(min: number): number {
      const baseMin = this.startHour * 60;
      const limitMin = this.endHour * 60;

      const clamped = Math.min(Math.max(min, baseMin), limitMin);
      const offsetIndex = clamped - baseMin;
      const visualMin = this.visualOffsets[offsetIndex] || 0;

      return (visualMin / 60) * this.rowPx;
    },

    /**
     * 時間帯の高さを計算する
     */
    hourRowStyle(hour: number): { height: string } {
      const startMin = hour * 60;
      const endMin = (hour + 1) * 60;
      const height = this.minuteToPx(endMin) - this.minuteToPx(startMin);

      return {
        height: `${Math.max(height, 1)}px`,
      };
    },

    panelStyle(ev: DayEvent): { top: string; height: string } {
      const baseMin = this.startHour * 60;
      const limitMin = this.endHour * 60;
      const { startMin, endMin } = this.getEventRange(ev);

      // 登録データにタイムライン外の時間が含まれていても、表示範囲内に収まるように調整する
      // 現在は 0:00 ~ 24:00 を表示しているため基本的にはそのままだが、念のため調整
      const clampedStart = Math.max(startMin, baseMin);
      const clampedEnd = Math.min(endMin, limitMin);

      const top = this.minuteToPx(clampedStart);
      const height = this.minuteToPx(clampedEnd) - this.minuteToPx(clampedStart);

      return {
        top: `${top}px`,
        height: `${Math.max(height, 1)}px`,
      };
    },

    onClickEvent(ev: DayEvent) {
      if (!this.clickable) { return; }
      // tslint:disable triple-equals
      if (this.$data.selectData ? ev.id == this.$data.selectData.id : false) { return; }
      this.$emit('click-event', { start: ev.start, end: ev.end });
      this.$data.selectData = ev;
    },
    isSelected(ev: DayEvent) {
      return this.$data.selectData ? ev.id == this.$data.selectData.id : false;
    },
    // DatePickerのheaderにクリック要素を挿入する
    injectDPheaderHandler() {
      const target = this.$el.querySelector('.day__month_btn') as HTMLElement;

      if (target && target.parentElement && !target.parentElement.classList.contains('day__month-wrapper')) {

        const wrapper = document.createElement('span');
        wrapper.className = 'day__month-wrapper';

        wrapper.addEventListener('click', () => {
          this.$emit('click-header');
        }, true);

        target.parentElement.insertBefore(wrapper, target);
        wrapper.appendChild(target);
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.injectDPheaderHandler();

      const el = this.$refs.timelineBody as HTMLElement;
      if (!el) { return; }
      const targetHour = 8;
      const offset = this.minuteToPx(targetHour * 60);
      el.scrollTop = offset > 0 ? offset : 0;
    });
  },
});
